/**
 * 微信授权服务
 * 处理微信授权相关逻辑
 */

import { logDebug, logError, logInfo, logWarn, showError } from '../utils/errorHandler';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:8080/api');

// 统一入口域名配置（用于突破微信授权域名和支付目录限制）
const WECHAT_ENTRY_DOMAIN = process.env.REACT_APP_WECHAT_ENTRY_DOMAIN || ''; // 例如：https://auth.entry.com
const WECHAT_PAY_ENTRY_DOMAIN = process.env.REACT_APP_WECHAT_PAY_ENTRY_DOMAIN || ''; // 例如：https://pay.entry.com

/**
 * 检测是否在微信环境
 */
export function isWechat(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  const result = ua.includes('micromessenger');
  logDebug('[WechatAuth] isWechat check', { ua, result });
  return result;
}

/**
 * 获取当前 URL 中的 code 参数（微信授权回调）
 */
export function getWechatCode(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('code');
}

/**
 * 获取当前 URL 中的 state 参数（微信授权回调，用于恢复支付流程）
 */
export function getWechatState(): string | null {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('state');
}

/**
 * 保存 state 参数到 localStorage（用于恢复支付流程）
 */
export function saveWechatState(state: string): void {
  localStorage.setItem('wechat_auth_state', state);
}

/**
 * 从 localStorage 获取保存的 state 参数
 */
export function getSavedWechatState(): string | null {
  return localStorage.getItem('wechat_auth_state');
}

/**
 * 清除保存的 state 参数
 */
export function clearWechatState(): void {
  localStorage.removeItem('wechat_auth_state');
}

/**
 * 获取当前域名（用于授权）
 */
export function getCurrentDomain(): string {
  return window.location.hostname;
}

/**
 * 获取授权所需的 AppID（从后端获取）
 * @param projectId 项目ID（可选，用于获取公募机构配置）
 */
async function getAuthAppId(projectId?: string): Promise<string | null> {
  try {
    const domain = getCurrentDomain();
    const params = new URLSearchParams({
      domain,
    });
    if (projectId) {
      params.append('projectId', projectId);
    }

    // 调用后端接口获取授权配置（这里需要后端提供一个接口返回 AppID）
    // 暂时从机构信息中获取，如果没有则返回 null
    return null;
  } catch (error) {
    console.error('[WechatAuth] Failed to get AppID:', error);
    return null;
  }
}

/**
 * 跳转到微信授权页面
 * @param appId 微信 AppID（必需）
 * @param projectId 项目ID（可选，用于获取公募机构配置）
 */
export function redirectToAuth(appId: string, projectId?: string): void {
  console.log('[WechatAuth] redirectToAuth called', {
    appId,
    projectId,
    isWechat: isWechat(),
    currentUrl: window.location.href,
    timestamp: new Date().toISOString()
  });
  
  logDebug('[WechatAuth] redirectToAuth called', { appId, projectId });
  
  if (!isWechat()) {
    console.warn('[WechatAuth] Not in WeChat environment, returning early');
    logWarn('[WechatAuth] Not in WeChat environment');
    showError('当前不在微信环境中', 'WechatAuth.redirectToAuth');
    return;
  }

  if (!appId) {
    console.error('[WechatAuth] AppID is required, returning early');
    logError('[WechatAuth] AppID is required');
    showError('微信授权配置缺失：AppID 为空', 'WechatAuth.redirectToAuth');
    return;
  }
  
  console.log('[WechatAuth] All checks passed, proceeding with redirect');

  // 如果配置了统一入口域名，跳转到入口域名进行授权
  if (WECHAT_ENTRY_DOMAIN) {
    const currentDomain = window.location.hostname;
    const currentPath = window.location.pathname + window.location.search;
    let redirectUrl = window.location.origin + currentPath;
    
    // 强制使用 HTTPS，确保微信授权回调使用正确的协议
    if (redirectUrl.startsWith('http://')) {
      redirectUrl = redirectUrl.replace('http://', 'https://');
      logInfo('[WechatAuth] Converted HTTP URL to HTTPS (entry domain)', { original: window.location.origin, converted: redirectUrl });
    }
    
    redirectUrl = encodeURIComponent(redirectUrl);
    // state 格式：{originalDomain}|{projectId}|{redirectUrl}|{appId}
    // 包含 appId 以确保回调时使用正确的 appID
    const state = encodeURIComponent(`${currentDomain}|${projectId || ''}|${redirectUrl}|${appId}`);
    
    // 在跳转前保存 projectId 到 localStorage，用于授权回调后恢复支付流程
    if (projectId) {
      saveWechatState(projectId);
      console.log('[WechatAuth] Saved state to localStorage before redirect (entry domain)', { projectId });
      logInfo('[WechatAuth] Saved state to localStorage before redirect (entry domain)', { projectId });
    }
    
    // 确保入口域名使用 HTTPS
    // 如果配置的是 HTTP，强制转换为 HTTPS（微信要求必须使用 HTTPS）
    let entryDomain = WECHAT_ENTRY_DOMAIN.trim();
    if (entryDomain.startsWith('http://')) {
      logWarn('[WechatAuth] Entry domain is HTTP, converting to HTTPS', { original: entryDomain });
      entryDomain = entryDomain.replace('http://', 'https://');
    } else if (!entryDomain.startsWith('https://')) {
      // 如果没有协议，默认添加 https://
      logWarn('[WechatAuth] Entry domain has no protocol, adding https://', { original: entryDomain });
      entryDomain = `https://${entryDomain}`;
    }
    
    // 跳转到入口域名的授权页面
    const entryAuthUrl = `${entryDomain}/auth/entry?appId=${appId}&state=${state}`;
    
    console.log('[WechatAuth] Entry domain redirect details', {
      originalEntryDomain: WECHAT_ENTRY_DOMAIN,
      normalizedEntryDomain: entryDomain,
      currentDomain,
      projectId,
      appId,
      entryAuthUrl,
      state,
      redirectUrl: decodeURIComponent(redirectUrl),
      timestamp: new Date().toISOString()
    });
    
    logInfo('[WechatAuth] Redirecting to entry domain for auth', { 
      originalEntryDomain: WECHAT_ENTRY_DOMAIN,
      normalizedEntryDomain: entryDomain,
      currentDomain,
      projectId,
      appId,
      entryAuthUrl
    });
    
    logInfo('[WechatAuth] About to redirect to entry domain', { entryAuthUrl });
    logInfo('[WechatAuth] Current window.location', {
      href: window.location.href,
      origin: window.location.origin,
      pathname: window.location.pathname,
      search: window.location.search
    });
    logInfo('[WechatAuth] document.body.style.display', { display: document.body.style.display });
    logInfo('[WechatAuth] User-Agent', { userAgent: navigator.userAgent });
    
    // 添加一个标记，用于检查是否真的跳转了
    const redirectStartTime = Date.now();
    localStorage.setItem('wechat_redirect_start_time', redirectStartTime.toString());
    logInfo('[WechatAuth] Set redirect start time', { redirectStartTime });
    
    // 使用 replace 而不是 href，避免页面历史记录问题，同时确保页面完全跳转
    try {
      logInfo('[WechatAuth] Calling window.location.replace now', { entryAuthUrl });
      window.location.replace(entryAuthUrl);
      logInfo('[WechatAuth] window.location.replace called successfully for entry domain');
      
      // 如果跳转成功，这个日志不会执行（因为页面会跳转）
      // 如果这个日志执行了，说明跳转失败
      setTimeout(() => {
        const redirectEndTime = Date.now();
        const elapsed = redirectEndTime - redirectStartTime;
        logError('[WechatAuth] WARNING: Page did not redirect after timeout', {
          elapsed,
          currentUrl: window.location.href,
          expectedUrl: entryAuthUrl
        });
      }, 1000);
    } catch (error) {
      logError('[WechatAuth] Failed to call window.location.replace for entry domain', error);
      // 如果 replace 失败，尝试使用 href
      logInfo('[WechatAuth] Falling back to window.location.href for entry domain');
      try {
        window.location.href = entryAuthUrl;
      } catch (hrefError) {
        logError('[WechatAuth] Failed to call window.location.href for entry domain', hrefError);
      }
    }
    return;
  }

  // 原有逻辑：直接跳转微信授权（如果没有配置入口域名）
  // 获取当前页面URL（使用根路径，避免路径问题）
  // 单页应用所有路径都应该重定向到根路径，避免授权回调后路径错误
  // 强制使用 HTTPS，确保微信授权回调使用正确的协议
  let currentUrl = window.location.origin + '/';
  if (currentUrl.startsWith('http://')) {
    currentUrl = currentUrl.replace('http://', 'https://');
    logInfo('[WechatAuth] Converted HTTP URL to HTTPS', { original: window.location.origin, converted: currentUrl });
  }
  
  const redirectUri = encodeURIComponent(currentUrl);
  const state = projectId || '';
  
  // 在跳转前保存 state 到 localStorage，用于授权回调后恢复支付流程
  if (projectId) {
    saveWechatState(projectId);
    logInfo('[WechatAuth] Saved state to localStorage before redirect', { projectId });
  }
  
  // 构建微信授权URL（非静默授权 snsapi_userinfo，需要用户明确确认）
  // 使用 snsapi_userinfo 需要用户确认授权，可以获取昵称、头像等信息
  const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_userinfo&state=${state}#wechat_redirect`;

  logInfo('[WechatAuth] Redirecting to WeChat auth', { 
    appId, 
    redirectUri: currentUrl,
    authUrl,
    currentUrl: window.location.href,
    timestamp: new Date().toISOString()
  });
  
  // 在跳转前记录，确保日志被输出
  console.log('[WechatAuth] About to redirect, this will cause page reload');
  console.log(`[WechatAuth] IMPORTANT: Authorization will use AppID=${appId} (this is the AppID that will appear in WeChat auth dialog)`);
  console.log('[WechatAuth] Auth URL:', authUrl);
  console.log('[WechatAuth] Current window.location:', {
    href: window.location.href,
    origin: window.location.origin,
    pathname: window.location.pathname,
    search: window.location.search
  });
  console.log('[WechatAuth] document.body.style.display:', document.body.style.display);
  console.log('[WechatAuth] About to call window.location.replace');
  
  // 使用 replace 而不是 href，避免页面历史记录问题，同时确保页面完全跳转
  // 跳转到授权页面（这会导致页面刷新，所有日志会清空，这是正常的）
  try {
    window.location.replace(authUrl);
    console.log('[WechatAuth] window.location.replace called successfully');
  } catch (error) {
    console.error('[WechatAuth] Failed to call window.location.replace', error);
    // 如果 replace 失败，尝试使用 href
    console.log('[WechatAuth] Falling back to window.location.href');
    window.location.href = authUrl;
  }
}

/**
 * 通过 code 获取 openId
 * @param code 微信授权码
 * @param domain 当前域名
 * @param projectId 项目ID（可选）
 */
export async function getOpenIdByCode(
  code: string,
  domain?: string,
  projectId?: string
): Promise<{ openId: string; unionId?: string; nickname?: string; avatar?: string }> {
  logDebug('[WechatAuth] getOpenIdByCode called', { code, domain, projectId });
  
  const params = new URLSearchParams({
    code,
  });

  if (domain) {
    params.append('domain', domain);
  }
  if (projectId) {
    params.append('projectId', projectId);
  }

  const url = `${API_BASE_URL}/auth/wechat?${params.toString()}`;
  logDebug('[WechatAuth] Request URL', { url });

  try {
    const response = await fetch(url);
    
    logDebug('[WechatAuth] Response status', { 
      status: response.status, 
      statusText: response.statusText,
      ok: response.ok 
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      logError('[WechatAuth] Response not OK', { 
        status: response.status, 
        statusText: response.statusText,
        body: errorText 
      });
      const error = new Error(`获取 openId 失败: HTTP ${response.status} ${response.statusText}\n响应: ${errorText}`);
      // 显示错误弹窗
      showError(error, 'WechatAuth.getOpenIdByCode');
      throw error;
    }

    const result = await response.json();
    logDebug('[WechatAuth] Response data', result);
    
    if (result.code !== 0) {
      logError('[WechatAuth] API error', { 
        code: result.code, 
        message: result.message,
        fullResponse: result 
      });
      const error = new Error(`获取 openId 失败: ${result.message || '未知错误'} (code: ${result.code})`);
      // 显示错误弹窗
      showError(error, 'WechatAuth.getOpenIdByCode');
      throw error;
    }

    logInfo('[WechatAuth] OpenId obtained', { 
      openId: result.data?.openId,
      nickname: result.data?.nickname,
      hasAvatar: !!result.data?.avatar
    });
    
    // 保存用户信息到 localStorage
    if (result.data?.openId) {
      saveOpenId(result.data.openId);
      if (result.data.nickname) {
        saveWechatNickname(result.data.nickname);
      }
      if (result.data.avatar) {
        saveWechatAvatar(result.data.avatar);
      }
    }
    
    return {
      openId: result.data?.openId || '',
      unionId: result.data?.unionId,
      nickname: result.data?.nickname,
      avatar: result.data?.avatar,
    };
    } catch (error) {
      logError('[WechatAuth] getOpenIdByCode failed', error);
      // 确保错误信息详细
      let finalError: Error;
      if (error instanceof Error) {
        finalError = error;
      } else {
        finalError = new Error(`获取 openId 失败: ${String(error)}`);
      }
      // 如果错误还没有显示过（通过 showError），则显示
      // 注意：如果上面的代码已经调用过 showError，这里不会重复显示（因为 showError 内部有防抖）
      if (!(error instanceof Error && (error as any).__errorShown)) {
        showError(finalError, 'WechatAuth.getOpenIdByCode');
        (finalError as any).__errorShown = true;
      }
      throw finalError;
    }
}

/**
 * 保存 openId 到 localStorage
 */
export function saveOpenId(openId: string): void {
  localStorage.setItem('wechat_openId', openId);
}

/**
 * 从 localStorage 获取 openId
 */
export function getOpenId(): string | null {
  return localStorage.getItem('wechat_openId');
}

/**
 * 清除 openId
 */
export function clearOpenId(): void {
  localStorage.removeItem('wechat_openId');
}


/**
 * 保存微信昵称到 localStorage
 */
export function saveWechatNickname(nickname: string): void {
  localStorage.setItem('wechat_nickname', nickname);
}

/**
 * 从 localStorage 获取微信昵称
 */
export function getWechatNickname(): string | null {
  return localStorage.getItem('wechat_nickname');
}

/**
 * 清除微信昵称
 */
export function clearWechatNickname(): void {
  localStorage.removeItem('wechat_nickname');
}

/**
 * 保存微信头像到 localStorage
 */
export function saveWechatAvatar(avatar: string): void {
  localStorage.setItem('wechat_avatar', avatar);
}

/**
 * 从 localStorage 获取微信头像
 */
export function getWechatAvatar(): string | null {
  return localStorage.getItem('wechat_avatar');
}

/**
 * 清除微信头像
 */
export function clearWechatAvatar(): void {
  localStorage.removeItem('wechat_avatar');
}

/**
 * 清除所有微信授权信息
 */
export function clearAllWechatAuth(): void {
  clearOpenId();
  clearWechatNickname();
  clearWechatAvatar();
  clearWechatState();
  logInfo('[WechatAuth] All WeChat auth info cleared');
}


